// DAAM Approvals — admin-only review queue for the P0 content lifecycle.
// Roles allowed by backend: super_admin | admin | owner | operator.
// Publishing is limited to Instagram in P0 (other platforms → 501 soft-fail).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Send, RefreshCcw, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import AppShell from "@/components/layout/AppShell";
import { useI18n } from "@/contexts/I18nContext";

export default function ApprovalsPage() {
  const { lang } = useI18n();
  // `t` is stable per-language render. Making it a plain function (recreated
  // every render) breaks the useEffect(load) dependency chain and causes an
  // infinite loading loop — hence useCallback with `lang` as its ONLY input.
  const t = useCallback(
    (ar, en) => (lang === "ar" ? ar : en),
    [lang],
  );
  const [posts, setPosts]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [audit, setAudit]   = useState([]);

  const load = useCallback(async () => {
    setLoad(true);
    setLoadError(null);
    try {
      const data = (await api.get("/posts/pending-approval")).data;
      // Defensive: the endpoint MUST return `{posts: [...]}`. Anything else
      // becomes a friendly error state instead of a phantom loader.
      if (!data || !Array.isArray(data.posts)) {
        setLoadError(t("استجابة غير متوقعة من الخادم", "Unexpected server response"));
        setPosts([]);
      } else {
        setPosts(data.posts);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg =
        status === 403 ? t("هذه الصفحة للمشرف فقط", "Admins only")
        : status === 401 ? t("انتهت جلستك — أعد تسجيل الدخول", "Session expired — please sign in")
        : detail?.message_ar || detail?.message_en
        || t("تعذّر تحميل الطابور", "Failed to load queue");
      setLoadError(msg);
      setPosts([]);
      toast.error(msg);
    } finally {
      // ALWAYS end the loading state, even on parse errors above.
      setLoad(false);
    }
  }, [t]);

  const loadAudit = useCallback(async () => {
    try {
      const data = (await api.get("/content-audit", { params: { limit: 30 } })).data;
      setAudit(Array.isArray(data?.events) ? data.events : []);
    } catch { /* silent — not fatal */ }
  }, []);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  async function approve(id) {
    setBusyId(id);
    try {
      await api.post(`/posts/${id}/approve`);
      toast.success(t("تم الاعتماد", "Approved"));
      await load(); await loadAudit();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      toast.error(d.message_ar || d.message_en || t("فشل الاعتماد", "Approve failed"));
    } finally { setBusyId(null); }
  }

  async function reject(id, reason) {
    if (!reason?.trim()) { toast.error(t("الرجاء ذكر سبب الرفض", "Please provide a reason")); return; }
    setBusyId(id);
    try {
      await api.post(`/posts/${id}/reject`, { reason });
      toast.success(t("تم الرفض", "Rejected"));
      setRejecting(null);
      await load(); await loadAudit();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      toast.error(d.message_ar || d.message_en || t("فشل الرفض", "Reject failed"));
    } finally { setBusyId(null); }
  }

  async function publish(id, platform, idempKey) {
    setBusyId(id);
    try {
      const r = (await api.post(`/posts/${id}/publish`, {
        platform,
        idempotency_key: idempKey,
      })).data;
      toast.success(
        t(`تم النشر إلى ${platform}`, `Published to ${platform}`)
        + (r.media_id ? ` — ${r.media_id}` : "")
      );
      setPublishing(null);
      await load(); await loadAudit();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      const code = d.code || "publish_failed";
      if (code === "platform_not_implemented") {
        toast.error(t("هذه المنصة غير مدعومة بعد", "Platform not supported yet"));
      } else {
        toast.error(d.message_ar || d.message_en || code);
      }
    } finally { setBusyId(null); }
  }

  return (
    <AppShell>
      <div dir={lang === "ar" ? "rtl" : "ltr"}
           className="mx-auto max-w-5xl px-4 py-8 space-y-6"
           data-testid="approvals-page">
        <header>
          <h1 className="text-3xl font-bold text-neutral-900">
            {t("طابور الموافقات", "Approvals queue")}
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            {t(
              "استعرض المسودات التي بانتظار المراجعة، اعتمد أو ارفض، ثم انشر إلى Instagram (المنصات الأخرى قريبًا).",
              "Review pending drafts, approve or reject, then publish to Instagram. Other platforms coming soon."
            )}
          </p>
        </header>

        <div className="flex items-center gap-2">
          <button data-testid="approvals-refresh"
                  onClick={() => { load(); loadAudit(); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-neutral-300 hover:bg-neutral-50 inline-flex items-center gap-2">
            <RefreshCcw className="w-3 h-3" />
            {t("تحديث", "Refresh")}
          </button>
          <span className="text-xs text-neutral-500">
            {t(`${posts.length} بانتظار المراجعة`, `${posts.length} awaiting review`)}
          </span>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 flex items-center gap-2"
               data-testid="approvals-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("جارٍ التحميل…", "Loading…")}
          </div>
        ) : loadError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center justify-between gap-3"
               data-testid="approvals-error">
            <span>{loadError}</span>
            <button onClick={() => { load(); loadAudit(); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-red-300 hover:bg-red-100 inline-flex items-center gap-1">
              <RefreshCcw className="w-3 h-3" />
              {t("إعادة المحاولة", "Retry")}
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500"
               data-testid="approvals-empty">
            {t("لا يوجد منشورات بانتظار المراجعة.", "No posts awaiting review.")}
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(p => (
              <ApprovalRow key={p.id} post={p} busy={busyId === p.id}
                           onApprove={() => approve(p.id)}
                           onOpenReject={() => setRejecting({ id: p.id, reason: "" })}
                           onOpenPublish={() => setPublishing({
                             id: p.id, platform: "instagram",
                             idem: crypto.randomUUID().replace(/-/g, "").slice(0, 24),
                           })}
                           t={t} lang={lang} />
            ))}
          </div>
        )}

        <section data-testid="content-audit-panel"
                 className="rounded-2xl border border-neutral-200 p-5 bg-white">
          <h2 className="text-lg font-bold text-neutral-900 mb-3">
            {t("سجل التدقيق", "Content audit log")}
          </h2>
          {audit.length === 0 ? (
            <div className="text-sm text-neutral-500">
              {t("لا توجد أحداث بعد.", "No events yet.")}
            </div>
          ) : (
            <ul className="space-y-1 text-xs font-mono max-h-72 overflow-y-auto">
              {audit.map(e => (
                <li key={e.id} className="text-neutral-700">
                  <span className="text-neutral-400">{e.timestamp?.slice(0, 19)}</span>
                  {" · "}
                  <b className={
                    e.event.startsWith("publish_success") ? "text-emerald-600"
                    : e.event.startsWith("publish_fail") ? "text-red-600"
                    : e.event === "approved" ? "text-emerald-600"
                    : e.event === "rejected" ? "text-red-600"
                    : "text-neutral-700"
                  }>
                    {e.event}
                  </b>
                  {e.platform && ` · ${e.platform}`}
                  {e.meta?.media_id_masked && ` · ${e.meta.media_id_masked}`}
                  {e.meta?.error_code && ` · ${e.meta.error_code}`}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Reject modal */}
        {rejecting && (
          <Modal onClose={() => setRejecting(null)}>
            <h3 className="text-lg font-bold text-neutral-900">
              {t("سبب الرفض", "Rejection reason")}
            </h3>
            <textarea data-testid="approvals-reject-reason"
                      value={rejecting.reason}
                      onChange={e => setRejecting(r => ({ ...r, reason: e.target.value }))}
                      rows={4} maxLength={500}
                      placeholder={t("اذكر سبب الرفض ليصحح صانع المحتوى المنشور",
                                     "Explain why so the creator can revise the post")}
                      className="w-full rounded-xl border border-neutral-300 p-3 text-sm mt-3
                                 focus:outline-none focus:ring-2 focus:ring-[#BC944A]/40" />
            <div className="flex gap-2 justify-end pt-3">
              <button onClick={() => setRejecting(null)}
                      className="text-sm px-4 py-2 rounded-full border border-neutral-300">
                {t("إلغاء", "Cancel")}
              </button>
              <button data-testid="approvals-reject-confirm"
                      onClick={() => reject(rejecting.id, rejecting.reason)}
                      disabled={busyId === rejecting.id}
                      className="text-sm px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
                {t("تأكيد الرفض", "Confirm reject")}
              </button>
            </div>
          </Modal>
        )}

        {/* Publish modal */}
        {publishing && (
          <Modal onClose={() => setPublishing(null)}>
            <h3 className="text-lg font-bold text-neutral-900">
              {t("النشر النهائي", "Publish now")}
            </h3>
            <p className="text-sm text-neutral-600 mt-2">
              {t(
                "سيتم النشر الفوري على المنصة المحددة. الإجراء لا يمكن التراجع عنه من هنا.",
                "The post will be published immediately. This action cannot be undone from here."
              )}
            </p>
            <label className="text-sm text-neutral-700 block mt-3">
              {t("المنصة", "Platform")}
              <select value={publishing.platform}
                      onChange={e => setPublishing(p => ({ ...p, platform: e.target.value }))}
                      data-testid="approvals-publish-platform"
                      className="mt-1 w-full rounded-xl border border-neutral-300 p-2 text-sm">
                <option value="instagram">Instagram</option>
                <option value="tiktok" disabled>TikTok — {t("قريباً", "coming soon")}</option>
                <option value="snapchat" disabled>Snapchat — {t("قريباً", "coming soon")}</option>
                <option value="youtube" disabled>YouTube — {t("قريباً", "coming soon")}</option>
              </select>
            </label>
            <div className="flex gap-2 justify-end pt-4">
              <button onClick={() => setPublishing(null)}
                      className="text-sm px-4 py-2 rounded-full border border-neutral-300">
                {t("إلغاء", "Cancel")}
              </button>
              <button data-testid="approvals-publish-confirm"
                      onClick={() => publish(publishing.id, publishing.platform, publishing.idem)}
                      disabled={busyId === publishing.id}
                      className="text-sm px-4 py-2 rounded-full bg-[#BC944A] text-white hover:bg-[#a58038] disabled:opacity-40 inline-flex items-center gap-2">
                <Send className="w-4 h-4" />
                {t("تأكيد ونشر", "Confirm & publish")}
              </button>
            </div>
          </Modal>
        )}
      </div>
    </AppShell>
  );
}

function ApprovalRow({ post, busy, onApprove, onOpenReject, onOpenPublish, t, lang }) {
  return (
    <article data-testid={`approval-row-${post.id}`}
             className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-2">
      <header className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-amber-400 text-amber-700 bg-amber-50 rounded-full">
          {t("بانتظار المراجعة", "Pending review")}
        </span>
        <h3 className="font-bold text-neutral-900 truncate">{post.title || t("(بدون عنوان)", "(no title)")}</h3>
        <span className="text-[11px] text-neutral-400 ms-auto">{post.created_at?.slice(0, 16).replace("T", " ")}</span>
      </header>
      <p className="text-sm text-neutral-700 whitespace-pre-wrap line-clamp-3">{post.content}</p>
      {post.media_urls?.[0] && (
        <img src={post.media_urls[0]} alt=""
             className="w-32 h-32 object-cover rounded-lg border border-neutral-200" />
      )}
      <div className="text-[11px] text-neutral-500">
        {t("المنصات:", "Platforms:")} {post.platforms?.join(", ") || "—"}
      </div>
      <div className="flex gap-2 pt-1 flex-wrap">
        <button data-testid={`approval-approve-${post.id}`}
                onClick={onApprove} disabled={busy}
                className="text-sm px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {t("اعتماد", "Approve")}
        </button>
        <button data-testid={`approval-reject-${post.id}`}
                onClick={onOpenReject} disabled={busy}
                className="text-sm px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40 inline-flex items-center gap-2">
          <XCircle className="w-4 h-4" />
          {t("رفض", "Reject")}
        </button>
        {post.approval_status === "approved" && (
          <button data-testid={`approval-publish-${post.id}`}
                  onClick={onOpenPublish} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded-full bg-[#BC944A] text-white hover:bg-[#a58038] disabled:opacity-40 inline-flex items-center gap-2 ms-auto">
            <Send className="w-4 h-4" />
            {t("نشر", "Publish")}
          </button>
        )}
      </div>
    </article>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">{children}</div>
    </div>
  );
}
